import { CloudOff, Loader2, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

export type SyncStatus =
  | "synced"
  | "pending"
  | "saving"
  | "refreshing"
  | "error";

interface SyncStatusIndicatorProps {
  status: SyncStatus;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  disabled?: boolean;
  className?: string;
  /** Compact mode (kept for API compat, now default behavior) */
  compact?: boolean;
}

/**
 * Minimal sync status indicator.
 *
 * - synced: green dot (+ refresh on hover)
 * - saving/refreshing: small spinner icon only
 * - pending: small warning dot
 * - error: visible destructive badge with label + refresh action
 */
export function SyncStatusIndicator({
  status,
  onRefresh,
  isRefreshing = false,
  disabled = false,
  className,
}: SyncStatusIndicatorProps) {
  const canRefresh = onRefresh && !disabled && !isRefreshing;

  // Synced: green dot, refresh on hover
  if (status === "synced") {
    if (canRefresh) {
      return (
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          className={cn(
            "flex items-center justify-center w-8 h-8 rounded-md group transition-all hover:bg-muted",
            className,
          )}
          title="Actualiser"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-success group-hover:hidden" />
          <RefreshCw className="h-3.5 w-3.5 text-muted-foreground hidden group-hover:block" />
        </button>
      );
    }

    return (
      <div className={cn("flex items-center justify-center w-8 h-8", className)}>
        <span className="h-1.5 w-1.5 rounded-full bg-success" />
      </div>
    );
  }

  // Saving / Refreshing: spinner only
  if (status === "saving" || status === "refreshing") {
    return (
      <div
        className={cn(
          "flex items-center justify-center w-8 h-8 text-muted-foreground",
          className,
        )}
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      </div>
    );
  }

  // Pending: small warning dot
  if (status === "pending") {
    return (
      <div className={cn("flex items-center justify-center w-8 h-8", className)}>
        <span className="h-1.5 w-1.5 rounded-full bg-warning" />
      </div>
    );
  }

  // Error: visible badge with label + refresh
  return (
    <button
      type="button"
      onClick={canRefresh ? onRefresh : undefined}
      disabled={!canRefresh}
      className={cn(
        "flex items-center gap-1.5 h-8 px-3 rounded-md text-body-sm transition-colors",
        "bg-destructive/10 text-destructive border border-destructive/20",
        canRefresh && "hover:bg-destructive/20 cursor-pointer",
        !canRefresh && "cursor-default",
        className,
      )}
      title={canRefresh ? "Réessayer la synchronisation" : "Erreur de synchronisation"}
    >
      <CloudOff className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">Erreur</span>
    </button>
  );
}
